import React, { useState } from 'react'
import { Button } from '../../../../components/ui/button'
import { Badge } from '../../../../components/ui/badge'
import ProfilePanel from './ProfilePanel'
import { LIST_PREVIEW_LIMIT } from './profileDetailSections'

import { formatSydneyDateTime } from '../../../../utils/sydneyDateTime'
const PREVIEW_LIMIT = LIST_PREVIEW_LIMIT

export default function ProfileScreeningSection({
  amlChecks,
  amlHeaderStatus,
  amlHeaderHint,
  isRunningAml,
  onRunAml,
  onViewAmlCheck,
  onCopyAmlLink,
}) {
  const [expanded, setExpanded] = useState(false)
  const sorted = [...(amlChecks || [])].sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  )
  const visible = expanded ? sorted : sorted.slice(0, PREVIEW_LIMIT)
  const hiddenCount = Math.max(0, sorted.length - PREVIEW_LIMIT)

  const bannerText = amlHeaderHint || (
    amlHeaderStatus && amlHeaderStatus !== 'CLEAR' && amlHeaderStatus !== 'N/A'
      ? `${amlHeaderStatus} on file — review screening history below`
      : sorted.length
        ? 'Latest screening clear · review history below'
        : 'No AML/PEP checks on record'
  )

  return (
    <ProfilePanel
      title="Screening"
      hint={sorted.length ? `${sorted.length} check${sorted.length === 1 ? '' : 's'}` : null}
      actions={
        <Button type="button" variant="outline" size="sm" disabled={isRunningAml} onClick={onRunAml}>
          {isRunningAml ? 'Running…' : 'Run check'}
        </Button>
      }
    >
      <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3.5 py-2.5 rounded-lg text-sm font-semibold mb-4">
        {bannerText}
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-500 m-0">No AML checks recorded.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-slate-500 text-xs">
                  <th className="p-2 font-semibold border-b border-slate-200">Run</th>
                  <th className="p-2 font-semibold border-b border-slate-200">Status</th>
                  <th className="p-2 font-semibold border-b border-slate-200">Outcome</th>
                  <th className="p-2 font-semibold border-b border-slate-200" />
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const clear = Number(c.result_count) === 0
                  const noMatch = c.analyst_decision === 'no_match'
                  const confirmed = c.analyst_decision === 'confirmed_match'
                  let outcome = `Results: ${c.result_count}`
                  if (noMatch) outcome = 'Reviewed, no match'
                  else if (clear) outcome = 'No PEP · No Sanctions'
                  else if (confirmed) outcome = `Confirmed · ${c.aml_derived_category || c.reason || 'match'}`
                  else if (c.reason) outcome = c.reason

                  return (
                    <tr key={c.id} className="border-b border-slate-100">
                      <td className="p-2.5 align-top whitespace-nowrap">
                        {formatSydneyDateTime(new Date(c.created_at))}
                      </td>
                      <td className="p-2.5 align-top">
                        <Badge
                          variant="outline"
                          className={
                            noMatch || clear
                              ? 'bg-green-50 text-green-800 border-green-300'
                              : confirmed
                                ? 'bg-amber-50 text-amber-900 border-amber-300'
                                : 'bg-amber-50 text-amber-900 border-amber-300'
                          }
                        >
                          {noMatch ? 'NO MATCH' : clear ? 'CLEAR' : confirmed ? 'CONFIRMED' : c.status || 'SUCCESS'}
                        </Badge>
                      </td>
                      <td className="p-2.5 align-top">
                        <span className={confirmed ? 'font-semibold' : ''}>{outcome}</span>
                        {c.analyst_reason && (
                          <div className="text-xs text-slate-500 mt-0.5">{c.analyst_reason}</div>
                        )}
                      </td>
                      <td className="p-2.5 align-top whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => onViewAmlCheck(c.id)}
                          className="text-teal-700 text-xs font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0 mr-3"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => onCopyAmlLink(c.id)}
                          className="text-slate-500 text-xs font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
                        >
                          Link
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 text-teal-700 text-sm font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            </button>
          )}
        </>
      )}
    </ProfilePanel>
  )
}
